import type { Product } from '@equinox/shared';

/**
 * One row's in-progress edit in the products table's batch "Edición en
 * tabla" mode — see docs/decisions/2026-07-31-products-table-edit-mode.md.
 * Pure data, no React: kept separate from `ProductsPage.tsx` so the dirty
 * tracking and validation rules are unit-testable without rendering a table.
 */
export interface ProductDraft {
  name: string;
  sku: string;
  /** `''` means "no category" — same convention `ProductFormModal` already uses. */
  categoryId: string;
  priceCents: number | null;
  /** Whatever `MoneyInput`/`PriceInput` last reported for the raw text — blank is "valid but incomplete", same as the create/edit modal. */
  priceValid: boolean;
  /** Optional unit cost. null is a legal, savable "unknown" — unlike price. */
  costCents: number | null;
  costValid: boolean;
  stockQty: number | null;
  /** Same "blank is valid" convention as `StockInput` — an untouched field is not an error. */
  stockValid: boolean;
  sortOrder: number;
  active: boolean;
  /** `''` means no image — same convention as `imageUrl.trim() || null` everywhere else in this app. */
  imageUrl: string;
}

export function draftFromProduct(product: Product): ProductDraft {
  return {
    name: product.name,
    sku: product.sku,
    categoryId: product.categoryId ?? '',
    priceCents: product.priceCents,
    priceValid: true,
    costCents: product.costCents ?? null,
    costValid: true,
    stockQty: product.stockQty,
    stockValid: true,
    sortOrder: product.sortOrder,
    active: product.active,
    imageUrl: product.imageUrl ?? '',
  };
}

/**
 * Whether a draft differs from its product's last-committed values —
 * whatever the row's Save loop would actually try to persist. Field-level
 * validity (`priceValid`/`stockValid`) is deliberately not part of this
 * check: an invalid-but-different value is still dirty (it needs the admin's
 * attention before it can be saved), it just fails
 * `rowValidationProblem` first.
 */
export function isDraftDirty(product: Product, draft: ProductDraft): boolean {
  return (
    draft.name.trim() !== product.name ||
    draft.sku.trim() !== product.sku ||
    (draft.categoryId || null) !== product.categoryId ||
    draft.priceCents !== product.priceCents ||
    draft.costCents !== (product.costCents ?? null) ||
    (draft.imageUrl.trim() || null) !== product.imageUrl ||
    draft.sortOrder !== product.sortOrder ||
    draft.active !== product.active ||
    draft.stockQty !== product.stockQty
  );
}

export type RowValidationProblem = 'nameOrSkuRequired' | 'invalidPrice' | 'invalidStock';

/**
 * Returns a problem *kind*, not an already-translated message — keeps this
 * module free of i18n so it stays trivial to unit test. The caller (
 * `ProductsPage`) maps each kind to the same translated strings the
 * create/edit modal already shows for the same problem
 * (`products.priceInvalid`, `products.stockInvalid`), plus one new one
 * specific to inline table editing (`products.tableEdit.nameSkuRequired`).
 *
 * A blank price or stock field is deliberately *not* "invalid" by itself —
 * `MoneyInput`/`StockInput` report `valid: true` for an empty field (their
 * blank-is-fine convention, shared with the create/edit modal, where a blank
 * stock field means "leave it alone"). Price has no such "leave it alone"
 * meaning for an existing product, though — every product needs a price —
 * so a blank price is still a blocking problem here even though the input
 * itself would not show its own inline error for it.
 */
export function rowValidationProblem(draft: ProductDraft): RowValidationProblem | null {
  if (!draft.name.trim() || !draft.sku.trim()) return 'nameOrSkuRequired';
  if (draft.priceCents == null || !draft.priceValid) return 'invalidPrice';
  // Cost may be blank (unknown), but garbled text in the field still blocks.
  if (!draft.costValid && draft.costCents != null) return 'invalidPrice';
  if (!draft.stockValid) return 'invalidStock';
  return null;
}
